import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Building2, Image as ImageIcon, Save, Loader2, Users, DollarSign, CreditCard,
  KeyRound, AlertCircle, ChevronRight, Settings as SettingsIcon, ArrowRight,
} from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();
  const { user, company, refreshAuth } = useAuth();
  const [companyForm, setCompanyForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';

  useEffect(() => {
    if (company) {
      setCompanyForm({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zip_code: company.zip_code || '',
        logo_url: company.logo_url || null,
        slug: company.slug || '',
      });
    }
  }, [company]);

  const saveCompany = async () => {
    if (!isAdmin) return toast.error('Only company admins can edit company settings');
    setSaving(true);
    try {
      await apiClient.put(`/api/companies/${company.id}`, companyForm);
      toast.success('Company settings updated');
      if (refreshAuth) await refreshAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (!companyForm) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
          <SettingsIcon className="w-7 h-7 text-sky-500" /> Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your company profile, branding, pricing, and team.
          {!isAdmin && (
            <span className="ml-2 text-amber-500">
              <AlertCircle className="inline w-4 h-4 mr-1" />
              You have view-only access — only company admins can edit.
            </span>
          )}
        </p>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="w-4 h-4 mr-2" />Company</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding"><ImageIcon className="w-4 h-4 mr-2" />Branding</TabsTrigger>
          <TabsTrigger value="shortcuts" data-testid="tab-shortcuts"><ArrowRight className="w-4 h-4 mr-2" />Shortcuts</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Profile</CardTitle>
              <CardDescription>This information appears on invoices, the customer portal, and reports.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} disabled={!isAdmin} data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={companyForm.email} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2 md:col-span-1">
                  <Label>Address</Label>
                  <Input value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value={companyForm.city} onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input value={companyForm.state} onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })} disabled={!isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>Zip Code</Label>
                  <Input value={companyForm.zip_code} onChange={(e) => setCompanyForm({ ...companyForm, zip_code: e.target.value })} disabled={!isAdmin} />
                </div>
              </div>
              {isAdmin && (
                <Button onClick={saveCompany} disabled={saving} className="btn-gradient-success" data-testid="save-company-button">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save changes
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Upload your company logo — appears on invoices, the login screen, and customer-facing pages.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Company Logo</Label>
                <ImageUpload
                  value={companyForm.logo_url}
                  onChange={(v) => setCompanyForm({ ...companyForm, logo_url: v })}
                  label="Upload logo"
                />
                <p className="text-xs text-muted-foreground">Recommended: square image, 512×512 px, PNG with transparency.</p>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label>Subdomain</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={companyForm.slug || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="acme-locksmith"
                    disabled={!isAdmin}
                    className="max-w-xs"
                    data-testid="company-slug-input"
                  />
                  <span className="text-sm text-muted-foreground">.mykeyboss.com</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Customers who visit your subdomain will see <b>your</b> logo + name on the login screen. Letters, digits, hyphens only.
                </p>
                {companyForm.slug && (
                  <p className="text-xs text-sky-400">
                    Preview branded login on this device:{' '}
                    <a href={`/login?brand=${companyForm.slug}`} className="underline" target="_blank" rel="noreferrer">
                      open preview →
                    </a>
                  </p>
                )}
              </div>
              {isAdmin && (
                <Button onClick={saveCompany} disabled={saving} className="btn-gradient-success" data-testid="save-branding-button">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save changes
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shortcuts" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ShortcutCard
              testid="goto-pricing"
              icon={DollarSign} iconColor="text-emerald-500" title="Pricing & Vehicle Lookup Rates"
              description="Adjust labor rate, key blank costs, after-hours markup, profit margin & more — used by the Pricing Calculator and Vehicle Lookup."
              onClick={() => navigate('/pricing-calculator?tab=settings')}
            />
            <ShortcutCard
              testid="goto-team"
              icon={Users} iconColor="text-sky-500" title="Team Members"
              description="Add/remove technicians, upload their photos, set roles, and pay splits."
              onClick={() => navigate('/team')}
            />
            <ShortcutCard
              testid="goto-billing"
              icon={CreditCard} iconColor="text-purple-500" title="Billing & Subscription"
              description="Manage your subscription plan and view past charges."
              onClick={() => navigate('/billing')}
            />
            <ShortcutCard
              testid="goto-forgot"
              icon={KeyRound} iconColor="text-amber-500" title="Change my password"
              description="Request a reset link via email or demo link if you forgot your password."
              onClick={() => navigate('/forgot-password')}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ShortcutCard({ icon: Icon, iconColor, title, description, onClick, testid }) {
  return (
    <Card className="cursor-pointer card-hover transition hover:scale-[1.01]" onClick={onClick} data-testid={testid}>
      <CardContent className="p-5 flex items-start gap-4">
        <Icon className={`w-8 h-8 ${iconColor} shrink-0`} />
        <div className="flex-1">
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}
